import { MobileDeviceFilters } from '@reliance/types/filters/MobileDeviceFilters';
import { MobileDevice } from '@reliance/types/models/MobileDevice';
import { useAsync } from 'react-async-hook';
import { IPaginatedResponse } from 'relcore-central';
import api from '../common/api';
import config from '../common/config';

export type MobileDeviceWithAppVersions = MobileDevice & {
  installedMessagingAppVersion?: string;
  assignedMessagingAppVersion?: string;
  installedKioskAppVersion?: string;
  assignedKioskAppVersion?: string;
  installedAdminAppVersion?: string;
  assignedAdminAppVersion?: string;
  installedInfoAppVersion?: string;
  assignedInfoAppVersion?: string;
};

export const useUnlockedMobileDevices = () =>
  useAsync<MobileDevice[]>(
    () => api.get<MobileDevice[]>('/mobile-device/unlocked').then((r) => r.data),
    []
  );

export const useMobileDevices = (filters?: MobileDeviceFilters | null) =>
  useAsync<IPaginatedResponse<MobileDeviceWithAppVersions> | null>(
    async () => {
      if (filters == null) return null;
      const response = await api.get<IPaginatedResponse<MobileDevice>>('/mobile-device', { params: filters });

      return {
        ...response.data,
        data: response.data.data.map((device) => ({
          ...device,
          installedMessagingAppVersion: device.appAssignments?.[config.appBundleIds.messaging]?.installedAppVersion,
          assignedMessagingAppVersion: device.appAssignments?.[config.appBundleIds.messaging]?.assignedAppVersion,
          installedKioskAppVersion: device.appAssignments?.[config.appBundleIds.kiosk]?.installedAppVersion,
          assignedKioskAppVersion: device.appAssignments?.[config.appBundleIds.kiosk]?.assignedAppVersion,
          installedAdminAppVersion: device.appAssignments?.[config.appBundleIds.admin]?.installedAppVersion,
          assignedAdminAppVersion: device.appAssignments?.[config.appBundleIds.admin]?.assignedAppVersion,
          installedInfoAppVersion: device.appAssignments?.[config.appBundleIds.info]?.installedAppVersion,
          assignedInfoAppVersion: device.appAssignments?.[config.appBundleIds.info]?.assignedAppVersion,
        })),
      };
    },
    [filters == null ? null : JSON.stringify(filters)]
  );

export const useMobileDeviceById = (deviceId: number | null | undefined) =>
  useAsync<MobileDeviceWithAppVersions | null>(
    async (currentDeviceId?: number | null | undefined) => {
      const id = currentDeviceId !== undefined ? currentDeviceId : deviceId;
      if (!id) {
        return null;
      }

      const response = await api.get<{ data: MobileDevice }>('/mobile-device/' + id);
      const device = response.data.data as MobileDevice;

      const deviceWithVersions: MobileDeviceWithAppVersions = {
        ...device,
        installedMessagingAppVersion:
          device.appAssignments?.[config.appBundleIds.messaging]?.installedAppVersion,
        assignedMessagingAppVersion:
          device.appAssignments?.[config.appBundleIds.messaging]?.assignedAppVersion,
        installedKioskAppVersion:
          device.appAssignments?.[config.appBundleIds.kiosk]?.installedAppVersion,
        assignedKioskAppVersion:
          device.appAssignments?.[config.appBundleIds.kiosk]?.assignedAppVersion,
        installedAdminAppVersion:
          device.appAssignments?.[config.appBundleIds.admin]?.installedAppVersion,
        assignedAdminAppVersion:
          device.appAssignments?.[config.appBundleIds.admin]?.assignedAppVersion,
        installedInfoAppVersion:
          device.appAssignments?.[config.appBundleIds.info]?.installedAppVersion,
        assignedInfoAppVersion:
          device.appAssignments?.[config.appBundleIds.info]?.assignedAppVersion,
      };

      return deviceWithVersions;
    },
    deviceId === null ? [] : [deviceId]
  );